import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { calcularRenta } from '../services/declaracionRenta/f22Service';
import { getModule } from '../components/DeclaracionRenta/F22/registry';
import { showSuccess, showError, showLoading } from '../utils/alerts';
import Swal from 'sweetalert2';

/**
 * Hook de la etapa de entrada de datos del formulario F-22.
 *
 * Solo se monta cuando ya se confirmó que NO existe una renta calculada para
 * esta empresa/año/régimen (ese chequeo lo hace ModuleRenderer antes) — por
 * eso este hook no recalcula en vivo ni hace autoguardado: no hay nada que
 * "guardar como borrador", el cálculo ocurre una sola vez, en el backend, al
 * hacer click en "Calcular Renta".
 *
 * @param {number} anioTributario
 * @param {string} regimen
 * @param {(savedRow: object) => void} onCalculado - se llama con la fila
 *   guardada (inputs + códigos calculados + pdf_url) al terminar el cálculo.
 */
const useF22Form = (anioTributario = 2026, regimen = '14d3', onCalculado) => {
  const { selectedCompany, token } = useAuth();

  const mod = useMemo(() => getModule(anioTributario, regimen), [anioTributario, regimen]);
  const createInitialStateFn = useMemo(() => mod?.createInitialState || (() => ({})), [mod]);

  const [data, setData] = useState(() => createInitialStateFn());
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const loadedRef = useRef(false);

  const idCompany = selectedCompany?.id_company || selectedCompany?.id;

  const updateField = useCallback((section, field, value) => {
    setData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    setDirty(true);
  }, []);

  const updateNestedField = useCallback((section, path, value) => {
    setData((prev) => {
      const updated = { ...prev, [section]: { ...prev[section] } };
      const keys = path.split('.');
      let current = updated[section];
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const match = key.match(/^(\w+)\[(\d+)\]$/);
        if (match) {
          const arrKey = match[1];
          const idx = parseInt(match[2]);
          current[arrKey] = [...current[arrKey]];
          current[arrKey][idx] = { ...current[arrKey][idx] };
          current = current[arrKey][idx];
        } else {
          current[key] = { ...current[key] };
          current = current[key];
        }
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
    setDirty(true);
  }, []);

  // "Calcular Renta": el backend calcula, guarda y genera el PDF en un solo paso.
  const calcular = useCallback(async () => {
    if (!idCompany || !token) return;
    setCalculando(true);
    try {
      showLoading('Calculando declaración de renta...');
      const result = await calcularRenta(idCompany, anioTributario, regimen, data);
      Swal.close();
      showSuccess('Declaración calculada correctamente');
      setDirty(false);
      if (onCalculado) onCalculado(result.data);
    } catch (err) {
      console.error('Error al calcular la renta:', err);
      Swal.close();
      showError('Error', err.response?.data?.message || 'No se pudo calcular la declaración');
    } finally {
      setCalculando(false);
    }
  }, [idCompany, anioTributario, regimen, data, token, onCalculado]);

  // Carga inicial: solo corre los dataLoaders del módulo (traen datos contables
  // reales para prellenar el formulario) — no hay declaración guardada que
  // buscar acá, eso ya lo descartó ModuleRenderer antes de montar este hook.
  useEffect(() => {
    if (loadedRef.current) return;
    if (!idCompany || !token) return;
    loadedRef.current = true;

    const load = async () => {
      setLoading(true);
      try {
        const loaders = mod?.dataLoaders || [];
        const loaderResults = await Promise.all(
          loaders.map((dl) =>
            dl.loader(idCompany, token).catch((err) => {
              console.error(`Error en dataLoader "${dl.key}":`, err);
              return null;
            })
          )
        );

        let formData = createInitialStateFn();
        loaders.forEach((dl, i) => {
          if (loaderResults[i]) formData = dl.apply(formData, loaderResults[i]);
        });

        setData(formData);
        setDirty(false);
      } catch (err) {
        console.error('Error al precargar datos de renta:', err);
      } finally {
        setLoaded(true);
        setLoading(false);
      }
    };

    load();
  }, [idCompany, token]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data,
    loading,
    loaded,
    dirty,
    calculando,
    updateField,
    updateNestedField,
    calcular,
    idCompany,
  };
};

export default useF22Form;
